/**
 * Danh sách TỪNG KHOẢN PHÁT SINH đã cộng vào lương.
 *
 * Trước đây phiếu chỉ có một ô "Phụ phí: 320.000đ" — đúng số nhưng không ai
 * kiểm được. Mục này trả lời đúng câu người ta hỏi: khoản đó đến từ đơn nào,
 * ngày nào, khách nào.
 */

import { useState } from "react";
import { vnd } from "../../constants";
import {
  groupSurchargeLines,
  priceTiers,
  shortDate,
  sortByShootDate,
  sumPay,
  surchargeLineLabel,
  surchargeLinePayNotes,
  type SurchargeLine,
} from "../../salaryDetail";
import {
  DetailSection,
  GroupHeading,
  LineOrderSwitch,
  LineTable,
  PayNotes,
  type LineOrder,
} from "./primitives";

export function SurchargesDetail({ lines }: { lines: SurchargeLine[] }) {
  const [order, setOrder] = useState<LineOrder>("date");

  if (!lines.length) return null;

  const groups = groupSurchargeLines(lines);
  const total = sumPay(lines);
  // Chỉ một loại thì hai kiểu xếp ra cùng một bảng — không bày nút.
  const byDate = groups.length > 1 && order === "date";

  return (
    <DetailSection
      testId="detail-surcharges"
      icon="➕"
      title="Phát sinh"
      countLabel={`${lines.length} khoản`}
      total={total}
    >
      {groups.length > 1 ? (
        <LineOrderSwitch value={order} onChange={setOrder} groupLabel="Theo loại" />
      ) : null}
      {byDate ? (
        <div data-testid="surcharges-by-date">
          <SurchargeTable lines={sortByShootDate(lines)} showKind />
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.key} data-testid={`surcharge-group-${g.key}`}>
              {/* Số lượng đọc y như cột SL bên dưới: dòng không ghi thì là 1. */}
              <GroupHeading
                label={g.label}
                count={g.count}
                pay={g.pay}
                tiers={priceTiers(
                  g.lines.map((l) => ({
                    qty: l.qty && l.qty > 0 ? l.qty : 1,
                    pay: l.pay ?? 0,
                  })),
                )}
              />
              <SurchargeTable lines={g.lines} />
            </div>
          ))}
        </div>
      )}
    </DetailSection>
  );
}

/**
 * Bảng các khoản. `showKind` là kiểu "Theo ngày": không còn đầu nhóm nói khoản
 * thuộc loại nào, nên máy tính thêm cột "Khoản", điện thoại ghi tên loại dưới
 * tên khách (cột bị ẩn cho khỏi tràn ngang).
 */
function SurchargeTable({
  lines,
  showKind = false,
}: {
  lines: SurchargeLine[];
  showKind?: boolean | undefined;
}) {
  return (
    <LineTable
      columns={[
        { key: "date", label: "Ngày" },
        { key: "code", label: "Mã đơn", className: "hidden md:table-cell" },
        { key: "customer", label: "Khách" },
        ...(showKind
          ? [{ key: "kind", label: "Khoản", className: "hidden md:table-cell" }]
          : []),
        { key: "qty", label: "SL", className: "text-right" },
        { key: "pay", label: "Tiền", className: "text-right" },
      ]}
    >
      {lines.map((line, i) => (
        <tr key={`${line.orderId}-${i}`}>
          <td className="whitespace-nowrap px-1 py-1.5 text-muted-foreground md:px-2">
            {shortDate(line.shootDate)}
          </td>
          <td className="hidden px-2 py-1.5 text-muted-foreground md:table-cell">
            {line.code || "—"}
          </td>
          <td className="px-1 py-1.5 md:px-2">
            {line.customer || "—"}
            {showKind ? (
              <span className="block text-xs text-muted-foreground md:hidden">
                {surchargeLineLabel(line)}
              </span>
            ) : null}
            {/*
              Chữ trên đơn hiện dưới tên khách, cỡ nhỏ: nó là bằng
              chứng đối chiếu ("Ghép phông cho ảnh 4x6"), nhưng không
              phải thứ đọc trước.
            */}
            {line.label ? (
              <span className="block text-xs text-muted-foreground">{line.label}</span>
            ) : null}
          </td>
          {showKind ? (
            <td className="hidden px-2 py-1.5 text-muted-foreground md:table-cell">
              {surchargeLineLabel(line)}
            </td>
          ) : null}
          <td className="px-1 py-1.5 text-right tabular-nums text-muted-foreground md:px-2">
            {line.qty && line.qty > 0 ? line.qty : 1}
          </td>
          <td className="px-1 py-1.5 text-right tabular-nums md:px-2">
            {vnd(line.pay ?? 0)}
            {/* Từ buổi chụp 01/10/2026 (quyết định 0179, E3): dòng
                tính theo công in phép tính như bảng đơn; dòng tiền
                phẳng đã chia thì nói chia mấy thợ. Dòng chưa chạm cơ
                chế thì không có chữ nào — bảng y như trước. */}
            <PayNotes notes={surchargeLinePayNotes(line)} />
          </td>
        </tr>
      ))}
    </LineTable>
  );
}
